import PopupWithDimmedBackground from 'components/PopupWithDimmedBackground';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Form, Icon } from 'semantic-ui-react';
import { playerApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { resizeImageUrl } from 'utils/image/imageResizeService';
import { validLeaderboardUsername } from 'utils/RegistrationValidator';

import Cross from '../../svg/Cross14.svg';

import { newMascots } from 'constants/Dashboard';
import { tryApi } from 'utils/api/common';
import styles from './EditProfileModal.module.css';

interface EditProfileModalProps {
  openModal: boolean;
  closeModal: () => void;
  currentAvatar: string;
}

const EditProfileModal = observer(
  ({ openModal, closeModal, currentAvatar }: EditProfileModalProps) => {
    const { currentUser, updateCurrentUser } = useUserStore();
    const { displayToast } = useUIStore();

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const [usernameEdited, setUsernameEdited] = useState(false);
    const [username, setUsername] = useState(currentUser?.friendlyName ?? '');

    const [doneButtonLoading, setDoneButtonLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [previewImage, setPreviewImage] = useState<string | null>(
      resizeImageUrl(currentAvatar, {
        width: 240,
        height: 240,
      }),
    );

    useEffect(() => {
      if (currentUser) {
        setUsername(currentUser.friendlyName ?? '');
      }
    }, [currentUser]);

    useEffect(() => {
      if (currentAvatar) {
        setPreviewImage(
          resizeImageUrl(currentAvatar, {
            width: 240,
            height: 240,
          }),
        );
      }
    }, [currentAvatar]);

    if (selectedFile) {
      const reader = new FileReader();

      reader.onload = (e) => {
        setPreviewImage(e.target ? (e.target.result as string) : null);
      };

      reader.readAsDataURL(selectedFile);
    }

    const selectNewAvatar = (avatar: string) => {
      setSelectedFile(null);
      setPreviewImage(avatar);
      setError(null);
    };

    const onSuccess = () => {
      setDoneButtonLoading(false);
      setError(null);
      setSelectedFile(null);
      updateCurrentUser();
      closeModal();
    };

    const handleOnClose = () => {
      setError(null);
      setSelectedFile(null);
      setPreviewImage(
        resizeImageUrl(currentAvatar, {
          width: 240,
          height: 240,
        }),
      );
      closeModal();
    };

    if (!openModal) {
      return null;
    }

    return (
      <PopupWithDimmedBackground margin={'3em 0 0em'}>
        <div className={styles.editProfileContainer}>
          <button className={styles.closeButton} onClick={handleOnClose}>
            <Cross />
          </button>
          <div
            className={`aboutUsTypographyHeaderMedium ${styles.editProfileHeader}`}
          >
            Edit profile
          </div>
          <div className={styles.avatarSelection}>
            <div className={styles.currentAvatar}>
              {previewImage && (
                <img src={previewImage} alt="User profile icon" />
              )}
              {(selectedFile || previewImage !== currentAvatar) && (
                <button className={styles.trashButton}>
                  <Icon
                    name="trash"
                    className={styles.trashIcon}
                    style={{ fontSize: '20px' }}
                    onClick={() => {
                      setError(null);
                      setSelectedFile(null);
                      setPreviewImage(currentAvatar);
                    }}
                  />
                </button>
              )}
            </div>
            <div className={styles.chooseAvatar}>
              <p className="textAlignLeft">Choose your icon</p>
              <div className={styles.avatarOptions}>
                {Object.values(newMascots).map((mascot, i) => (
                  <button
                    key={i}
                    className={`${styles.avatarOption} ${mascot === previewImage ? styles.selectedAvatarOption : ''}`}
                    onClick={() => {
                      selectNewAvatar(mascot);
                    }}
                  >
                    <img src={mascot} alt="User profile icon option" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className={styles.formHeader}>
              <img
                className={styles.formIcon}
                src={`${ASSET_PREFIX}/images/account/details/picture-logo.png`}
              />
              <h5 className={styles.formHeaderTitle}>
                Upload your own picture
              </h5>
            </div>

            <Form className={styles.form} id="buttonForm">
              {selectedFile && (
                <div className={styles.selectedFileName}>
                  {selectedFile.name}
                </div>
              )}
              <Form.Field className={styles.formField}>
                <label className={styles.formLabel} htmlFor="fileUploadButton">
                  Choose file
                </label>
                <input
                  id="fileUploadButton"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setSelectedFile(e.target.files ? e.target.files[0] : null);
                    setError(null);
                  }}
                  className={styles.formInput}
                />
              </Form.Field>
            </Form>
          </div>
          <div>
            <div className={styles.formHeader}>
              <img
                className={styles.formIcon}
                src={`${ASSET_PREFIX}/images/account/details/username.png`}
              />
              <h5 className={styles.formHeaderTitle}>Change your username</h5>
            </div>
            <Form style={{ flexShrink: 0 }}>
              <Form.Field
                error={usernameEdited && !validLeaderboardUsername(username)}
              >
                <input
                  type="text"
                  autoComplete="off"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError(null);
                  }}
                  onBlur={() => setUsernameEdited(true)}
                  className={styles.inputUsername}
                />
              </Form.Field>
            </Form>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}

          <div>
            <RoundedButton
              loading={doneButtonLoading}
              disabled={!validLeaderboardUsername(username)}
              onClick={async () => {
                setDoneButtonLoading(true);

                // Updated avatar to mascot
                if (
                  previewImage !== currentAvatar &&
                  Object.values(newMascots).includes(previewImage || '')
                ) {
                  await tryApi(
                    playerApiClient.auth.updateDetails({
                      body: {
                        player: {
                          avatarLocation: previewImage,
                          friendlyName: username,
                        },
                      },
                    }),
                  );

                  displayToast({
                    title: 'Successfully updated details.',
                    timeout: 3500,
                  });

                  return onSuccess();
                }

                // Updated avatar to own picture
                if (selectedFile) {
                  await tryApi(
                    playerApiClient.auth.updateDetails({
                      body: {
                        player: {
                          friendlyName: username,
                        },
                      },
                    }),
                  );
                  const photoUploadResult = await tryApi(
                    playerApiClient.auth.uploadAvatarIcon({
                      body: { image: selectedFile },
                    }),
                  );

                  if (photoUploadResult.status !== HttpStatusCode.OK) {
                    setError(
                      'body' in photoUploadResult
                        ? photoUploadResult.body.message
                        : 'Could not upload image, file must be below 6 Mb.',
                    );
                    setDoneButtonLoading(false);
                  }
                }

                displayToast({
                  title: 'Successfully updated details.',
                  timeout: 3500,
                });

                onSuccess();
              }}
              colorStyle="OutlineDark"
              fullWidth={false}
              style={{ textTransform: 'none' }}
            >
              Save changes
            </RoundedButton>
          </div>
        </div>
      </PopupWithDimmedBackground>
    );
  },
);

export default EditProfileModal;
